// Tag: a status pill. Category colours are data (hex), so the fill is that hex at low alpha
// and the label is the hex, lifted toward white until it clears the contrast floor. A `tone`
// names a meaning shared across the app; `variant="code"` is an identifier, not a state.
// docs/operations/design-standard.md, "Colour and contrast".

import React, { useLayoutEffect, useRef } from 'react';
import { CATEGORY, contrast, over } from './categoryColors';
import { CONTROL_MOTION, EASE, FOCUS, MS, PRESS, STATE, TOUCH, reducedMotion } from './motion';

const rgb = (hex: string): [number, number, number] | null => {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const n = parseInt(full, 16);
  return [n >> 16, (n >> 8) & 255, n & 255];
};

/** The hex at `alpha`, for a tinted fill under its own colour. */
export const tint = (hex: string, alpha: number): string => {
  const c = rgb(hex);
  return c ? `rgba(${c[0]},${c[1]},${c[2]},${alpha})` : 'transparent';
};

const toHex = (c: number[]) => `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
const mix = (a: number[], b: number[], t: number) => a.map((v, i) => Math.round(v + (b[i] - v) * t));

// The worst case a tag sits on: its own hex at 20% over the track step, the lightest surface
// that carries tags (a Select well). Clearing it here clears it on every darker step.
const TRACK = '#323232';
const FLOOR = 60;
const inks = new Map<string, string>();

/** The hex as a label colour that clears the secondary-text floor (Lc 60) on its own tint. */
export const readable = (hex: string): string => {
  const hit = inks.get(hex);
  if (hit) return hit;
  const c = rgb(hex);
  if (!c) return 'var(--on-surface)';
  const ground = over(toHex(c), TRACK, 0.2);
  // Whole steps, not a float accumulator: 20 x 0.05 overshoots 1 and would never try white.
  let out = '#ffffff';
  for (let i = 0; i <= 20; i += 1) {
    const m = mix(c, [255, 255, 255], i / 20);
    if (contrast(toHex(m), ground) >= FLOOR) {
      out = i === 0 ? hex : `rgb(${m[0]},${m[1]},${m[2]})`;
      break;
    }
  }
  inks.set(hex, out);
  return out;
};

export type TagTone = 'neutral' | 'info' | 'good' | 'warn' | 'bad';

/** Meanings shared across the app, for a status whose colour is not data of its own. */
export const TONE_HEX: Record<TagTone, string> = {
  neutral: CATEGORY.neutral.ink.hex,
  info: CATEGORY.blue.ink.hex,
  good: CATEGORY.green.ink.hex,
  warn: CATEGORY.yellow.ink.hex,
  bad: CATEGORY.red.ink.hex,
};

const ACCENT = 'var(--color-primary, #ffcc33)';

const SIZE = {
  md: 'h-5 px-2 text-xs leading-4',
  sm: 'h-4 px-1.5 text-[11px] leading-4',
};

export interface TagProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onClick' | 'color' | 'style'> {
  /** A category colour as data (Spark's stages). Wins over `tone`. */
  hex?: string;
  /** A shared meaning. `accent` is "this needs you" (a receipt to review), in the instance's accent. */
  tone?: TagTone | 'accent';
  /** `code`: an identifier or count, not a state. Recessed into whatever it sits on. */
  variant?: 'status' | 'code';
  /** A serial or UID that should align as code. */
  mono?: boolean;
  /** `sm` for a tag inside a line of text or a two-line cell. */
  size?: 'md' | 'sm';
  /** Breathes while the state waits on someone. Under reduced motion it holds a deeper fill instead. */
  attention?: boolean;
  /** A status you can act on (open its workflow). Draws as a button. */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}

export const Tag: React.FC<TagProps> = ({
  hex,
  tone = 'neutral',
  variant = 'status',
  mono = false,
  size = 'md',
  attention = false,
  onClick,
  title,
  className = '',
  children,
  ...rest
}) => {
  const ref = useRef<HTMLElement>(null);
  const code = variant === 'code';
  const colour = hex || (tone === 'accent' ? '' : TONE_HEX[tone] || TONE_HEX.neutral);
  const style = code
    ? undefined
    : colour
      ? ({ backgroundColor: tint(colour, 0.18), color: readable(colour), '--tag-hue': tint(colour, 0.3) } as React.CSSProperties)
      : ({
          backgroundColor: `color-mix(in srgb, ${ACCENT} 15%, transparent)`,
          color: ACCENT,
          '--tag-hue': `color-mix(in srgb, ${ACCENT} 30%, transparent)`,
        } as React.CSSProperties);

  // A status that changes in place lands: a short spring down from a touch larger. Its colour
  // fades either way, so reduced motion keeps the feedback without the movement.
  const said = code
    ? 'code'
    : `${colour || 'accent'}|${typeof children === 'string' || typeof children === 'number' ? children : ''}`;
  const last = useRef(said);
  useLayoutEffect(() => {
    if (last.current === said) return undefined;
    last.current = said;
    const el = ref.current;
    if (!el || reducedMotion()) return undefined;
    // The colours stay in both lists: the reflow below is when the new fill takes effect, and
    // `transition: none` there would snap it instead of fading it.
    const colours = `background-color ${MS.quick}ms ${EASE.emphasized}, color ${MS.quick}ms ${EASE.emphasized}, --np-layer ${MS.quick}ms ${EASE.emphasized}`;
    el.style.transition = `transform 0ms, ${colours}`;
    el.style.transform = 'scale(1.08)';
    void el.offsetWidth;
    el.style.transition = `transform ${MS.spring}ms ${EASE.springBouncy}, ${colours}`;
    el.style.transform = '';
    const t = window.setTimeout(() => { if (el.isConnected) el.style.transition = ''; }, MS.spring + 40);
    return () => window.clearTimeout(t);
  }, [said]);

  // Black at 30% sits one ladder step below any host, so a code never vanishes on a table row
  // that rises to the control step under the pointer (a raised pill did).
  const look = code ? 'bg-[rgb(0_0_0/0.3)] text-on-surface' : '';
  const breath = attention && !code
    ? "isolate after:pointer-events-none after:absolute after:inset-0 after:-z-10 after:rounded-full after:bg-[color:var(--tag-hue)] after:opacity-50 after:content-[''] motion-safe:after:animate-glowBreathe"
    : '';
  const base = `relative inline-flex min-w-0 max-w-full items-center whitespace-nowrap rounded-full font-semibold ${SIZE[size]} ${
    code || mono ? 'tabular-nums' : ''
  } ${mono ? 'font-mono' : ''} ${look} ${breath}`;
  const label = <span className="truncate">{children}</span>;

  if (onClick) {
    const key = (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        style={style}
        className={`${base} cursor-pointer select-none ${STATE} ${CONTROL_MOTION} ${PRESS} ${FOCUS} ${TOUCH} ${className}`}
        {...rest}
      >
        {label}
      </button>
    );
    // A title on a <button> replaces its accessible name (web/CLAUDE.md), so it hangs on a
    // box-less span around it instead.
    return title ? <span title={title} className="contents">{key}</span> : key;
  }
  return (
    <span
      ref={ref as React.Ref<HTMLSpanElement>}
      title={title}
      style={style}
      className={`${base} transition-[background-color,color] duration-quick ease-emphasized ${className}`}
      {...rest}
    >
      {label}
    </span>
  );
};

export default Tag;
